import { useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';

interface NavbarProps {
  isScrolled: boolean;
}

export default function Navbar({ isScrolled }: NavbarProps) {
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [isServicesDropdownOpen, setIsServicesDropdownOpen] = useState(false);
  const location = useLocation();
  const navigate = useNavigate();
  const isHomePage = location.pathname === '/';

  const scrollToSection = (id: string) => {
    if (!isHomePage) {
      navigate('/');
      setTimeout(() => {
        const element = document.getElementById(id);
        if (element) {
          element.scrollIntoView({ behavior: 'smooth' });
        }
      }, 100);
    } else {
      const element = document.getElementById(id);
      if (element) {
        element.scrollIntoView({ behavior: 'smooth' });
      }
    }
    setIsMobileMenuOpen(false);
  };

  const handleNavigation = (path: string) => {
    navigate(path);
    setIsMobileMenuOpen(false);
    setIsServicesDropdownOpen(false);
  };

  return (
    <nav
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        isScrolled ? 'bg-white shadow-md' : 'bg-transparent'
      }`}
    >
      <div className="max-w-7xl mx-auto px-6 lg:px-8">
        <div className="flex items-center justify-between h-20">
          <button onClick={() => handleNavigation('/')} className="flex items-center cursor-pointer">
            <img
              src="https://static.readdy.ai/image/8ae829f1f3ae863ba0771f16530de4a8/83c892b906c51bf7ea2e0c2eee7aa7ce.png"
              alt="R3D Nuisibles Logo"
              className="h-14 w-auto object-contain transition-all duration-300"
            />
          </button>

          <div className="hidden md:flex items-center space-x-8">
            <button
              onClick={() => scrollToSection('accueil')}
              className={`text-sm font-medium transition-colors whitespace-nowrap cursor-pointer ${
                isScrolled ? 'text-gray-700 hover:text-brand-600' : 'text-white hover:text-brand-300'
              }`}
            >
              Accueil
            </button>
            
            <div 
              className="relative"
              onMouseEnter={() => setIsServicesDropdownOpen(true)}
              onMouseLeave={() => setIsServicesDropdownOpen(false)}
            >
              <button
                className={`text-sm font-medium transition-colors whitespace-nowrap cursor-pointer flex items-center gap-1 ${
                  isScrolled ? 'text-gray-700 hover:text-brand-600' : 'text-white hover:text-brand-300'
                }`}
              >
                Services
                <i className="ri-arrow-down-s-line text-base"></i>
              </button>
              
              {isServicesDropdownOpen && (
                <div className="absolute top-full left-0 mt-2 w-56 bg-white rounded-lg shadow-xl py-2 border border-gray-100">
                  <button
                    onClick={() => handleNavigation('/deratisation')}
                    className="w-full text-left px-4 py-3 text-sm text-gray-700 hover:bg-brand-50 hover:text-brand-600 transition-colors cursor-pointer flex items-center gap-3"
                  >
                    <i className="ri-bug-line text-lg"></i>
                    Dératisation
                  </button>
                  <button
                    onClick={() => handleNavigation('/desinsectisation')}
                    className="w-full text-left px-4 py-3 text-sm text-gray-700 hover:bg-brand-50 hover:text-brand-600 transition-colors cursor-pointer flex items-center gap-3"
                  >
                    <i className="ri-contrast-drop-line text-lg"></i>
                    Désinsectisation
                  </button>
                  <button
                    onClick={() => handleNavigation('/desinfection')}
                    className="w-full text-left px-4 py-3 text-sm text-gray-700 hover:bg-brand-50 hover:text-brand-600 transition-colors cursor-pointer flex items-center gap-3"
                  >
                    <i className="ri-plant-line text-lg"></i>
                    Désinfection
                  </button>
                </div>
              )}
            </div>

            <button
              onClick={() => scrollToSection('valeurs')}
              className={`text-sm font-medium transition-colors whitespace-nowrap cursor-pointer ${
                isScrolled ? 'text-gray-700 hover:text-brand-600' : 'text-white hover:text-brand-300'
              }`}
            >
              Valeurs
            </button>
            <button
              onClick={() => scrollToSection('references')}
              className={`text-sm font-medium transition-colors whitespace-nowrap cursor-pointer ${
                isScrolled ? 'text-gray-700 hover:text-brand-600' : 'text-white hover:text-brand-300'
              }`}
            >
              Références
            </button>
            <button
              onClick={() => scrollToSection('temoignages')}
              className={`text-sm font-medium transition-colors whitespace-nowrap cursor-pointer ${
                isScrolled ? 'text-gray-700 hover:text-brand-600' : 'text-white hover:text-brand-300'
              }`}
            >
              Témoignages
            </button>
            <button
              onClick={() => scrollToSection('contact')}
              className="bg-brand-600 text-white px-6 py-2.5 rounded-lg text-sm font-medium hover:bg-brand-700 transition-colors whitespace-nowrap cursor-pointer"
            >
              Contact
            </button>
          </div>

          <button
            onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
            className="md:hidden p-2 cursor-pointer"
          >
            <i
              className={`ri-menu-line text-2xl ${isScrolled ? 'text-gray-700' : 'text-white'}`}
            ></i>
          </button>
        </div>
      </div>

      {isMobileMenuOpen && (
        <div className="md:hidden bg-white shadow-lg">
          <div className="px-6 py-4 space-y-3">
            <button
              onClick={() => scrollToSection('accueil')}
              className="block w-full text-left text-sm font-medium text-gray-700 hover:text-brand-600 py-2 cursor-pointer"
            >
              Accueil
            </button>
            
            <div className="space-y-2">
              <div className="text-sm font-medium text-gray-900 py-2">Services</div>
              <button
                onClick={() => handleNavigation('/deratisation')}
                className="block w-full text-left text-sm text-gray-700 hover:text-brand-600 py-2 pl-4 cursor-pointer"
              >
                • Dératisation
              </button>
              <button
                onClick={() => handleNavigation('/desinsectisation')}
                className="block w-full text-left text-sm text-gray-700 hover:text-brand-600 py-2 pl-4 cursor-pointer"
              >
                • Désinsectisation
              </button>
              <button
                onClick={() => handleNavigation('/desinfection')}
                className="block w-full text-left text-sm text-gray-700 hover:text-brand-600 py-2 pl-4 cursor-pointer"
              >
                • Désinfection
              </button>
            </div>

            <button
              onClick={() => scrollToSection('valeurs')}
              className="block w-full text-left text-sm font-medium text-gray-700 hover:text-brand-600 py-2 cursor-pointer"
            >
              Valeurs
            </button>
            <button
              onClick={() => scrollToSection('references')}
              className="block w-full text-left text-sm font-medium text-gray-700 hover:text-brand-600 py-2 cursor-pointer"
            >
              Références
            </button>
            <button
              onClick={() => scrollToSection('temoignages')}
              className="block w-full text-left text-sm font-medium text-gray-700 hover:text-brand-600 py-2 cursor-pointer"
            >
              Témoignages
            </button>
            <button
              onClick={() => scrollToSection('contact')}
              className="block w-full text-left bg-brand-600 text-white px-6 py-2.5 rounded-lg text-sm font-medium hover:bg-brand-700 transition-colors cursor-pointer"
            >
              Contact
            </button>
          </div>
        </div>
      )}
    </nav>
  );
}